import React, { useEffect, useState, useRef } from 'react';
import { SendIcon, XIcon, RefreshCwIcon } from 'lucide-react';
import { useEsosa } from './EsosaContext';
import AssistantAvatar from './AssistantAvatar';
import LanguageSelector from './LanguageSelector';
const ChatWindow: React.FC = () => {
  const {
    messages,
    sendMessage,
    isLoading,
    closeAssistant,
    userType,
    clearMessages,
    currentLanguage
  } = useEsosa();
  const [input, setInput] = useState('');
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Auto-scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  }, [messages]);
  // Focus input when chat opens
  useEffect(() => {
    inputRef.current?.focus();
  }, []);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (input.trim() && !isLoading) {
      sendMessage(input);
      setInput('');
    }
  };
  // Get placeholder text based on language
  const getPlaceholder = () => {
    switch (currentLanguage) {
      case 'pidgin':
        return 'Type your message here...';
      case 'edo':
        return 'Type we message ye...';
      default:
        return 'Type your message here...';
    }
  };
  // Get header text based on language and user type
  const getHeaderText = () => {
    const baseText = {
      english: 'Esosa AI Assistant',
      pidgin: 'Esosa AI Assistant',
      edo: 'Esosa AI Assistant'
    };
    const roleText = {
      english: {
        user: 'User Support',
        admin: 'Admin Support',
        super_admin: 'Super Admin Support',
        guest: 'Guest Support'
      },
      pidgin: {
        user: 'User Support',
        admin: 'Admin Support',
        super_admin: 'Super Admin Support',
        guest: 'Visitor Support'
      },
      edo: {
        user: 'User Support',
        admin: 'Admin Support',
        super_admin: 'Super Admin Support',
        guest: 'Visitor Support'
      }
    };
    return `${baseText[currentLanguage]} - ${roleText[currentLanguage][userType]}`;
  };
  return <div className="fixed bottom-24 right-6 w-80 sm:w-96 bg-white rounded-lg shadow-xl overflow-hidden z-50 border border-gray-200 flex flex-col max-h-[70vh]" data-id="element-231">
      {/* Header */}
      <div className="bg-green-700 text-white px-4 py-3 flex items-center justify-between" data-id="element-232">
        <div className="flex items-center" data-id="element-233">
          <AssistantAvatar size="sm" data-id="element-234" />
          <h3 className="ml-2 font-medium text-sm" data-id="element-235">{getHeaderText()}</h3>
        </div>
        <div className="flex items-center space-x-2" data-id="element-236">
          <button onClick={clearMessages} className="text-white hover:text-green-200 transition-colors" aria-label="Reset conversation" data-id="element-237">
            <RefreshCwIcon size={16} data-id="element-238" />
          </button>
          <button onClick={closeAssistant} className="text-white hover:text-green-200 transition-colors" aria-label="Close assistant" data-id="element-239">
            <XIcon size={18} data-id="element-240" />
          </button>
        </div>
      </div>
      {/* Language selector */}
      <div className="bg-green-50 px-4 py-2 border-b border-green-100" data-id="element-241">
        <LanguageSelector data-id="element-242" />
      </div>
      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-green-50" data-id="element-243">
        {messages.map(message => <div key={message.id} className={`flex ${message.sender === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-244">
            <div className={`max-w-[80%] rounded-lg px-4 py-2 ${message.sender === 'user' ? 'bg-green-700 text-white rounded-br-none' : 'bg-white border border-green-200 rounded-bl-none'}`} data-id="element-245">
              {message.sender === 'assistant' && <div className="flex items-center mb-1" data-id="element-246">
                  <AssistantAvatar size="sm" data-id="element-247" />
                  <span className="ml-2 text-xs font-medium text-green-700" data-id="element-248">
                    Esosa
                  </span>
                </div>}
              <p className="text-sm whitespace-pre-wrap" data-id="element-249">{message.content}</p>
              <div className="text-right" data-id="element-250">
                <span className={`text-xs ${message.sender === 'user' ? 'text-green-200' : 'text-gray-400'}`} data-id="element-251">
                  {message.timestamp.toLocaleTimeString([], {
                hour: '2-digit',
                minute: '2-digit'
              })}
                </span>
              </div>
            </div>
          </div>)}
        {isLoading && <div className="flex justify-start" data-id="element-252">
            <div className="bg-white border border-green-200 rounded-lg rounded-bl-none max-w-[80%] px-4 py-3" data-id="element-253">
              <div className="flex items-center space-x-2" data-id="element-254">
                <AssistantAvatar size="sm" data-id="element-255" />
                <div className="flex space-x-1" data-id="element-256">
                  <div className="w-2 h-2 rounded-full bg-green-500 animate-bounce" style={{
                animationDelay: '0ms'
              }} data-id="element-257"></div>
                  <div className="w-2 h-2 rounded-full bg-green-500 animate-bounce" style={{
                animationDelay: '150ms'
              }} data-id="element-258"></div>
                  <div className="w-2 h-2 rounded-full bg-green-500 animate-bounce" style={{
                animationDelay: '300ms'
              }} data-id="element-259"></div>
                </div>
              </div>
            </div>
          </div>}
        <div ref={messagesEndRef} data-id="element-260" />
      </div>
      {/* Input */}
      <form onSubmit={handleSubmit} className="border-t border-green-100 p-4 bg-white" data-id="element-261">
        <div className="flex items-center" data-id="element-262">
          <input ref={inputRef} type="text" value={input} onChange={e => setInput(e.target.value)} placeholder={getPlaceholder()} className="flex-1 border border-green-300 rounded-l-md px-4 py-2 focus:outline-none focus:ring-2 focus:ring-green-500 focus:border-transparent" disabled={isLoading} data-id="element-263" />
          <button type="submit" className={`bg-green-700 text-white px-4 py-2 rounded-r-md ${isLoading || !input.trim() ? 'opacity-50 cursor-not-allowed' : 'hover:bg-green-800'}`} disabled={isLoading || !input.trim()} data-id="element-264">
            <SendIcon size={18} data-id="element-265" />
          </button>
        </div>
      </form>
    </div>;
};
export default ChatWindow;